export type CommonPoInboxSelectorScalarV1 = Readonly<{
  kind: "string";
  value: string;
}>;

export type CommonPoInboxRowIntentV1 = "why_proposed" | "why_needs_review";

export type CommonPoInboxRowContextV1 = Readonly<{
  version: "CommonPoInboxRowContext.v1";
  surfaceId: "po-inbox";
  receiptId: string;
  intent: CommonPoInboxRowIntentV1;
  dimensions: readonly Readonly<{
    field: string;
    value: CommonPoInboxSelectorScalarV1;
  }>[];
  state: Readonly<{
    basis: "persisted";
    dirty: false;
    approvalPending: false;
  }>;
}>;

const PO_INBOX_RECEIPT_ID = /^rvr3_[a-f0-9]{64}$/;
const EXPLAINABLE_PO_INBOX_STATUSES = new Set([
  "Needs Review",
  "Needs Review: New POs to Place",
]);
const REQUIRED_IDENTITY_FIELDS = [
  "purchase_order_id",
  "sku",
  "sku_name",
  "location",
  "supplier",
] as const;

const boundedIdentityText = (value: unknown): string | undefined =>
  typeof value === "string" &&
  value.length > 0 &&
  value === value.trim() &&
  value.length <= 512 &&
  !/\p{Cc}/u.test(value)
    ? value
    : undefined;

const selectorDimension = (
  field: string,
  value: string,
): Readonly<{
  field: string;
  value: CommonPoInboxSelectorScalarV1;
}> =>
  Object.freeze({
    field,
    value: Object.freeze({ kind: "string" as const, value }),
  });

export const isExplainablePoInboxStatus = (status: unknown): status is string =>
  typeof status === "string" && EXPLAINABLE_PO_INBOX_STATUSES.has(status);

/** Immutable row eligibility. Transient planner state must not remove the
 * Explain action; the activation boundary reports why the saved proposal
 * cannot be inspected yet and leaves the same action available for retry. */
export function isPoInboxProposalCandidate(rowValue: unknown): boolean {
  if (!rowValue || typeof rowValue !== "object" || Array.isArray(rowValue)) {
    return false;
  }
  const row = rowValue as Readonly<Record<string, unknown>>;
  return (
    row.purchase_order_id === "TBD" &&
    isExplainablePoInboxStatus(row.status) &&
    row.approved !== true &&
    REQUIRED_IDENTITY_FIELDS.every((field) =>
      Boolean(boundedIdentityText(row[field])),
    )
  );
}

export function isExplainablePoInboxProposal(
  input: Readonly<{
    row: unknown;
    dirty: boolean;
    approvalPending: boolean;
  }>,
): boolean {
  return (
    !input.dirty &&
    !input.approvalPending &&
    isPoInboxProposalCandidate(input.row)
  );
}

export function createPoInboxRowContext(
  input: Readonly<{
    receiptId: unknown;
    intent: unknown;
    row: Readonly<Record<string, unknown>>;
    dirty: boolean;
    approvalPending: boolean;
  }>,
): CommonPoInboxRowContextV1 | undefined {
  if (
    typeof input.receiptId !== "string" ||
    !PO_INBOX_RECEIPT_ID.test(input.receiptId) ||
    (input.intent !== "why_proposed" && input.intent !== "why_needs_review") ||
    !isExplainablePoInboxProposal(input)
  ) {
    return undefined;
  }

  const dimensions: Array<
    Readonly<{
      field: string;
      value: CommonPoInboxSelectorScalarV1;
    }>
  > = [];
  for (const field of REQUIRED_IDENTITY_FIELDS) {
    const value = boundedIdentityText(input.row[field]);
    if (!value) return undefined;
    dimensions.push(selectorDimension(field, value));
  }
  const status = boundedIdentityText(input.row.status);
  if (!status || !isExplainablePoInboxStatus(status)) return undefined;
  dimensions.push(selectorDimension("status", status));

  return Object.freeze({
    version: "CommonPoInboxRowContext.v1" as const,
    surfaceId: "po-inbox" as const,
    receiptId: input.receiptId,
    intent: input.intent,
    dimensions: Object.freeze(dimensions),
    state: Object.freeze({
      basis: "persisted" as const,
      dirty: false as const,
      approvalPending: false as const,
    }),
  });
}

const identityValue = (
  context: CommonPoInboxRowContextV1,
  field: string,
): string | undefined =>
  context.dimensions.find((dimension) => dimension.field === field)?.value
    .value;

export function poInboxRowPrompt(context: CommonPoInboxRowContextV1): string {
  const poId = identityValue(context, "purchase_order_id") ?? "unknown";
  const sku = identityValue(context, "sku") ?? "unknown";
  const skuName = identityValue(context, "sku_name") ?? "unknown";
  const location = identityValue(context, "location") ?? "unknown";
  const supplier = identityValue(context, "supplier") ?? "unknown";
  return `Why this proposal? Explain why this saved purchase-order proposal needs review. PO ID ${poId}; SKU ${sku} (${skuName}); location ${location}; supplier ${supplier}. Reconcile its quantity, unit cost, total cost, and dates, and state any source gaps.`;
}
